// CB:BEGIN pm-signup-component
import React, { useState, useEffect, useRef } from 'react';
import { useHeadlessAuth, silentTicketSignIn } from '../auth/headlessSignup';
import { useUser, useAuth } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../lib/getApiUrl';

interface PropertyManagerSignupProps {
  onNavigate: (screen: string) => void;
}

const BRAND_GREEN = '#123c3b';

const PropertyManagerSignup: React.FC<PropertyManagerSignupProps> = ({ onNavigate }) => {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const { silentSignIn } = useHeadlessAuth();

  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<{[k:string]: string}>({});
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeSms, setAgreeSms] = useState(true);
  const [complianceVersions, setComplianceVersions] = useState<{ currentTermsVersion: string; currentPrivacyVersion: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addressRef = useRef<HTMLInputElement>(null);
  const apiBase = getApiBaseUrl();

  // Clear stale role cache so switching roles works cleanly
  useEffect(() => { try { localStorage.removeItem('cb.usertype'); } catch {} }, []);

  // Google Places autocomplete for Company Address
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_API_KEY;
    if (!apiKey) return;

    const attachAutocomplete = () => {
      const inputEl = addressRef.current;
      if (!inputEl || !(window as any).google?.maps?.places) return;
      if ((inputEl as any).__gacAttached) return;
      (inputEl as any).__gacAttached = true;

      const ac = new (window as any).google.maps.places.Autocomplete(inputEl, {
        types: ['address'],
        fields: ['formatted_address'],
        componentRestrictions: { country: 'us' },
      });

      if (!document.getElementById('pac-style-overrides')) {
        const style = document.createElement('style');
        style.id = 'pac-style-overrides';
        style.textContent = `.pac-container { z-index: 9999 !important; } .pac-icon, .pac-icon-marker { display: none !important; } .pac-logo::after { display: none !important; } .pac-item { padding: 6px 12px; }`;
        document.head.appendChild(style);
      }

      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place?.formatted_address) {
          if (addressRef.current) addressRef.current.value = place.formatted_address;
          setCompanyAddress(place.formatted_address);
        }
      });
    };

    const scriptId = 'google-places-script';
    if ((window as any).google?.maps?.places) {
      attachAutocomplete();
    } else if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__cbPlacesReady`;
      script.async = true;
      (window as any).__cbPlacesReady = () => { attachAutocomplete(); delete (window as any).__cbPlacesReady; };
      document.head.appendChild(script);
    } else {
      const poll = setInterval(() => {
        if ((window as any).google?.maps?.places) { clearInterval(poll); attachAutocomplete(); }
      }, 100);
      setTimeout(() => clearInterval(poll), 10000);
    }

    return () => { if (addressRef.current) delete (addressRef.current as any).__gacAttached; };
  }, []);

  // Fetch current ToS/Privacy versions
  useEffect(() => {
    fetch(`${apiBase.replace(/\/$/, '')}/api/compliance/versions`)
      .then((r) => r.json())
      .then((d) => setComplianceVersions(d))
      .catch(() => setComplianceVersions({ currentTermsVersion: '1.0', currentPrivacyVersion: '1.0' }));
  }, [apiBase]);

  // 2FA state
  const [showSecondFactorCode, setShowSecondFactorCode] = useState(false);
  const [secondFactorSignIn, setSecondFactorSignIn] = useState<any>(null);
  const [secondFactorCode, setSecondFactorCode] = useState('');
  const [pendingClerkId, setPendingClerkId] = useState<string | null>(null);

  const validate = () => {
    if (!companyName.trim()) return 'Company / Organization Name is required';
    if (!companyAddress.trim()) return 'Company Address is required';
    if (!email.includes('@')) return 'Valid email is required';
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return 'Valid phone number is required';
    if (!agreeTerms) return 'You must agree to the Terms & Conditions';
    if (!agreePrivacy) return 'You must agree to the Privacy Policy';
    if (!isSignedIn) {
      if (password.length < 8) return 'Password must be at least 8 characters';
      if (!/[A-Za-z]/.test(password)) return 'Password must contain at least one letter';
      if (!/\d/.test(password)) return 'Password must contain at least one number';
      // eslint-disable-next-line no-useless-escape
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(password)) return 'Password must contain at least one special character (!@#$%^&*)';
      if (password !== confirm) return 'Passwords do not match';
    }
    return null;
  };

  function handlePasswordChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    if (name === 'password') setPassword(value); else setConfirm(value);
    const pwd = name === 'password' ? value : password;
    const conf = name === 'passwordConfirm' ? value : confirm;
    const next: {[k:string]: string} = {};
    if (pwd && conf && pwd !== conf) next.passwordConfirm = 'Passwords do not match';
    setPasswordErrors(prev => ({ ...prev, ...next, ...(next.passwordConfirm ? {} : { passwordConfirm: '' }) }));
  }

  async function runPostSigninSteps(clerkId: string) {
    await fetch(`${apiBase.replace(/\/$/, '')}/api/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: clerkId, email, account_type: 'PROPERTY_MANAGER' }),
    }).catch(() => {});

    // Cache role immediately to avoid role-gate redirect
    try { localStorage.setItem('cb.usertype', 'property_manager'); } catch {}
    // Store signup data for onboarding modal pre-fill
    try {
      localStorage.setItem('cb.pm.signup', JSON.stringify({
        companyName: companyName.trim(),
        companyAddress: companyAddress.trim(),
        phone: phone.trim(),
      }));
    } catch {}
  }

  async function handleSubmit() {
    if (busy) return;
    setError(null);
    const v = validate();
    if (v) { setError(v); return; }
    setBusy(true);
    try {
      let clerkId = user?.id || '';

      if (!isSignedIn) {
        const resp = await fetch(`${apiBase.replace(/\/$/, '')}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            firstName: companyName.trim() || email.split('@')[0] || 'Manager',
            lastName: '',
            phone,
            role: 'PROPERTY_MANAGER',
            textOptIn: agreeSms,
          }),
        });
        if (!resp.ok) {
          let body: any = null;
          try { body = await resp.json(); } catch {}
          const msg = body?.message || body?.error || 'Registration failed. Please try again.';
          throw Object.assign(new Error(msg), { code: body?.error || 'register_failed' });
        }
        const data = await resp.json();
        clerkId = data.userId;

        let signed = await silentTicketSignIn(email, { firstName: companyName.trim() || email.split('@')[0] || 'Manager', lastName: '', phone });
        if (!signed) {
          try {
            const clerk: any = (window as any).Clerk;
            if (clerk) {
              await clerk.load();
              const si = await clerk.client.signIn.create({ identifier: email, password });
              if (si?.status === 'complete' && si?.createdSessionId) {
                await clerk.setActive({ session: si.createdSessionId });
                signed = true;
              } else if (si?.status === 'needs_second_factor') {
                const factors = si?.supportedSecondFactors || [];
                const emailCodeFactor = factors.find((f: { strategy: string }) => f.strategy === 'email_code');
                if (emailCodeFactor) {
                  await si.prepareSecondFactor({ strategy: 'email_code', emailAddressId: emailCodeFactor.emailAddressId });
                  setPendingClerkId(clerkId);
                  setSecondFactorSignIn(si);
                  setShowSecondFactorCode(true);
                  setBusy(false);
                  return;
                }
              }
            }
          } catch (e) {
            console.warn('Password sign-in fallback failed:', e);
          }
        }
      }

      await runPostSigninSteps(clerkId);
      onNavigate('pm-dashboard');
    } catch (e: any) {
      const CODE_MESSAGES: Record<string, string> = {
        form_password_pwned:        'This password was found in a data breach. Please choose a different, unique password.',
        form_identifier_exists:     'An account with this email already exists. Try signing in instead.',
        form_password_too_short:    'Password must be at least 8 characters.',
        form_password_no_uppercase: 'Password must contain at least one uppercase letter.',
        form_password_no_lowercase: 'Password must contain at least one lowercase letter.',
        form_password_no_digit:     'Password must contain at least one number.',
        form_password_no_special_char: 'Password must contain at least one special character.',
        form_param_format_invalid:  'One or more fields are invalid. Please check your email and password.',
      };
      const friendly = CODE_MESSAGES[(e as any)?.code] ?? e?.message ?? 'Signup failed';
      setError(friendly);
    } finally {
      setBusy(false);
    }
  }

  async function handleSecondFactorSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!secondFactorSignIn || !pendingClerkId || busy) return;
    setError(null);
    setBusy(true);
    try {
      const attempt = await secondFactorSignIn.attemptSecondFactor({ strategy: 'email_code', code: secondFactorCode });
      if (attempt?.status !== 'complete' || !attempt?.createdSessionId) {
        setError('Invalid or expired code. Please try again.');
        return;
      }
      const clerk: any = (window as any).Clerk;
      if (clerk) await clerk.setActive({ session: attempt.createdSessionId });
      const clerkId = pendingClerkId;
      setShowSecondFactorCode(false);
      setSecondFactorSignIn(null);
      setPendingClerkId(null);
      setSecondFactorCode('');

      await runPostSigninSteps(clerkId);
      onNavigate('pm-dashboard');
    } catch (err: any) {
      setError(err?.message ?? 'Verification failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (showSecondFactorCode) {
    return (
      <div className="container">
        <h2 style={{ marginBottom: 16 }}>Verify your email</h2>
        <p style={{ color: '#666', marginBottom: 16 }}>We sent a verification code to your email. Enter it below.</p>
        <form onSubmit={handleSecondFactorSubmit}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="Verification code"
            value={secondFactorCode}
            onChange={(e) => setSecondFactorCode(e.target.value)}
            style={{ width: '100%', maxWidth: 280, padding: 10, marginBottom: 12, border: '1px solid #ccc', borderRadius: 6 }}
          />
          {error && <p style={{ color: '#c00', marginBottom: 8 }}>{error}</p>}
          <button type="submit" disabled={busy} style={{ padding: '10px 20px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 6 }}>
            Verify and continue
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <style>{`
        #pm-terms-checkbox, #pm-privacy-checkbox, #pm-sms-checkbox {
          width: 16px !important; height: 16px !important;
          min-width: 16px !important; min-height: 16px !important;
          max-width: 16px !important; max-height: 16px !important;
          transform: scale(0.75) !important; transform-origin: left center !important;
          appearance: none !important; -webkit-appearance: none !important;
          border: 1px solid #d1d5db !important; border-radius: 2px !important;
          background-color: white !important; cursor: pointer !important; margin: 0 !important;
        }
        #pm-terms-checkbox:checked, #pm-privacy-checkbox:checked, #pm-sms-checkbox:checked {
          background-color: white !important; border-color: #d1d5db !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 10l3 3 6-6'/%3e%3c/svg%3e") !important;
          background-size: 100% 100% !important; background-position: center !important;
          background-repeat: no-repeat !important; box-shadow: none !important;
        }
      `}</style>

      {/* Header */}
      <header style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          <button
            onClick={() => onNavigate('pm-landing')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, fontWeight: 800, color: BRAND_GREEN, letterSpacing: '-0.03em' }}
          >
            CoreBids
          </button>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              onClick={() => onNavigate('pm-landing')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#64748b', padding: '8px 12px' }}
            >
              Back
            </button>
            <button
              onClick={() => onNavigate('sign-in')}
              style={{ background: 'none', border: `1px solid ${BRAND_GREEN}`, cursor: 'pointer', fontSize: 14, color: BRAND_GREEN, padding: '8px 16px', borderRadius: 8, fontWeight: 600 }}
            >
              Log In
            </button>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 640, margin: '48px auto', padding: '0 24px 64px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', marginBottom: 8, letterSpacing: '-0.02em' }}>
            Create your Property Manager account
          </h1>
          <p style={{ color: '#64748b', fontSize: 16 }}>
            Manage your portfolio, collect bids, and track every project — all in one place.
          </p>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 32, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label htmlFor="pm-companyName" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>
              Company / Organization Name *
            </label>
            <input
              id="pm-companyName"
              className="form-control"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Acme Property Management"
            />
          </div>

          <div>
            <label htmlFor="pm-companyAddress" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>
              Company Address *
            </label>
            <input
              id="pm-companyAddress"
              ref={addressRef}
              className="form-control"
              value={companyAddress}
              onChange={(e) => setCompanyAddress(e.target.value)}
              placeholder="Start typing your address…"
              autoComplete="new-password"
            />
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>
              Start typing — select your address from the Google suggestions that appear.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label htmlFor="pm-email" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>
                Email *
              </label>
              <input
                id="pm-email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label htmlFor="pm-phone" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>
                Phone Number *
              </label>
              <input
                id="pm-phone"
                className="form-control"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-5555"
              />
            </div>
          </div>

          {!isSignedIn && (
            <div style={{ marginTop: 4 }}>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 14 }}>Password *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={handlePasswordChange}
                  className="form-control"
                  placeholder="Create a password"
                  autoComplete="new-password"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="btn btn-secondary"
                  style={{ padding: '8px 12px', fontSize: 12, minWidth: 60 }}
                >
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
              <label style={{ display: 'block', marginBottom: 4, marginTop: 8, fontWeight: 600, fontSize: 14 }}>
                Confirm Password *
              </label>
              <input
                type={showPw ? 'text' : 'password'}
                name="passwordConfirm"
                value={confirm}
                onChange={handlePasswordChange}
                className="form-control"
                placeholder="Re-enter password"
                autoComplete="new-password"
              />
              {passwordErrors.passwordConfirm && (
                <p style={{ color: '#dc2626', fontSize: 12, marginTop: 2 }}>{passwordErrors.passwordConfirm}</p>
              )}
            </div>
          )}

          <div style={{ marginTop: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, fontWeight: 400 }}>
              <input
                id="pm-terms-checkbox"
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                style={{ width: 16, height: 16, minWidth: 16, cursor: 'pointer', margin: 0, flexShrink: 0 }}
              />
              <span style={{ color: '#1f2937' }}>
                I agree to the{' '}
                <a href="/terms" style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">
                  Terms &amp; Conditions
                </a>{' '}
                *
              </span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: 13, fontWeight: 400 }}>
              <input
                id="pm-privacy-checkbox"
                type="checkbox"
                checked={agreePrivacy}
                onChange={(e) => setAgreePrivacy(e.target.checked)}
                style={{ width: 16, height: 16, minWidth: 16, cursor: 'pointer', margin: 0, flexShrink: 0 }}
              />
              <span style={{ color: '#1f2937' }}>
                I agree to the{' '}
                <a href="/privacy" style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>{' '}
                *
              </span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 400 }}>
              <input
                id="pm-sms-checkbox"
                type="checkbox"
                checked={agreeSms}
                onChange={(e) => setAgreeSms(e.target.checked)}
                style={{ width: 16, height: 16, minWidth: 16, cursor: 'pointer', margin: 0, flexShrink: 0 }}
              />
              <span style={{ color: '#1f2937' }}>I agree to receive text messages</span>
            </label>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.3 }}>
              By entering your number &amp; checking I agree to receive text messages, you agree to receive automated texts from CoreBids, Inc. at 877-570-7485 for project updates and support. Msg &amp; data rates may apply. Text STOP to cancel.{' '}
              <a href="/terms" style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">Terms</a>{' '}and{' '}
              <a href="/privacy" style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </div>
          </div>

          {error && <div style={{ color: '#dc2626', fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={busy}
              style={{ background: BRAND_GREEN, borderColor: BRAND_GREEN, minWidth: 160, fontSize: 15, fontWeight: 700 }}
            >
              {busy ? 'Creating account…' : 'Create Account'}
            </button>
          </div>

          <p style={{ textAlign: 'center', fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            Already have an account?{' '}
            <button
              onClick={() => onNavigate('sign-in')}
              style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline' }}
            >
              Log in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
// CB:END pm-signup-component

export default PropertyManagerSignup;
